/* ------------------------------------------------------------------------- *
 * Idenza Navigator — launch page
 *
 * The screen that opens when someone lands in Navigator. It routes to the two
 * risk command centers (merchant acquiring, stablecoin programs) and surfaces
 * what needs attention across the platform so the page has a job beyond
 * navigation.
 *
 * Self contained. One file, one default export, no local imports, no CSS
 * import, no Tailwind. Uses react-icons, already in the Navigator bundle, so it
 * adds no new dependency.
 *
 *   import NavigatorLaunch from "./NavigatorLaunch";
 *   <NavigatorLaunch onOpen={(key, href) => navigate(href)} />
 *
 * props
 *   data        override the summary numbers, same shape as DEMO_DATA
 *   routes      { acquiring, stablecoin } paths that override the defaults in
 *               DEMO_DATA. Pass realm-qualified paths from Navigator so the
 *               card shows the same link it navigates to.
 *   onOpen      (key, href) => void   key: "acquiring" | "stablecoin"
 *   onQuickLink (href, link) => void   link carries { external } for the
 *               case management jump
 *   user        display name in the greeting; omit for the neutral heading
 *   demoShell   true renders a mock Navigator sidebar around the page, for
 *               screenshots and the walkthrough. Leave false inside the app.
 * ------------------------------------------------------------------------- */

import React, { useEffect } from "react";
import {
  FiArrowRight, FiChevronRight, FiAlertTriangle, FiShield, FiClock,
  FiShoppingBag, FiDollarSign,FiTrendingUp,
} from "react-icons/fi";
import { FaFileAlt, FaFlask, FaList, FaBriefcase } from "react-icons/fa";

/* =============================== tokens =============================== */
/* Matched to the live Navigator shell: bright blue sidebar, navy-to-blue
 * banner, near-white blue-grey page. */
const T = {
  navy: "#10214A",
  blue: "#2563EB",
  blueBright: "#2F6FED",
  blueSoft: "#E8F0FE",
  purple: "#6D28D9",
  purpleSoft: "#EFE9FD",
  red: "#DC2626",
  redSoft: "#FDECEC",
  amber: "#C2680B",
  amberSoft: "#FDF2E3",
  green: "#15803D",
  greenSoft: "#E7F4EC",
  teal: "#0E5F63",
  tealSoft: "#E3F0F0",
  page: "#F4F7FD",
  card: "#FFFFFF",
  line: "#E3E9F5",
  lineSoft: "#EFF3FA",
  text: "#0F1B33",
  muted: "#64748B",
  sans: 'ui-sans-serif, system-ui, "Segoe UI", Helvetica, Arial, sans-serif',
  mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
};

/* ========================= scoped stylesheet ========================== */
const STYLE_ID = "idz-navigator-launch-styles";
const css = `
.idzl-root { box-sizing: border-box; }
.idzl-root *, .idzl-root *::before, .idzl-root *::after { box-sizing: inherit; }

.idzl-shell { display: flex; min-height: 100%; }
.idzl-side { width: 260px; flex: 0 0 260px; padding: 18px 14px; }
.idzl-page { flex: 1; min-width: 0; }
.idzl-wrap { max-width: 1360px; margin: 0 auto; padding: 20px 24px 32px; }

.idzl-banner { border-radius: 16px; padding: 22px 26px; display: flex; align-items: center;
  gap: 18px; flex-wrap: wrap; background: linear-gradient(100deg, #10214A 0%, #1D3F8F 46%, #2F6FED 100%); }
.idzl-logo { width: 46px; height: 46px; border-radius: 10px; background: #0B1834; color: #fff;
  display: flex; align-items: center; justify-content: center; font-family: Georgia, "Times New Roman", serif;
  font-size: 24px; font-weight: 700; flex: 0 0 auto; }

.idzl-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-top: 22px; }
.idzl-metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; margin-top: 16px; }
.idzl-links { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-top: 12px; }
@media (max-width: 1100px) {
  .idzl-cards { grid-template-columns: 1fr; }
  .idzl-links { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .idzl-side { display: none; }
}
@media (max-width: 620px) {
  .idzl-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .idzl-links { grid-template-columns: 1fr; }
  .idzl-wrap { padding: 14px 14px 24px; }
  .idzl-row { flex-wrap: wrap; }
  .idzl-row > span:nth-child(2) { flex: 1 0 100%; }
  .idzl-row-btn { margin-left: 0; }
}

.idzl-card { background: #fff; border: 1px solid var(--idzl-line); border-radius: 14px; overflow: hidden;
  display: flex; flex-direction: column; transition: box-shadow 140ms ease, transform 140ms ease; }
.idzl-card-open { cursor: pointer; }
.idzl-card-open:hover { box-shadow: 0 10px 28px rgba(16, 33, 74, 0.10); transform: translateY(-2px); }

.idzl-eyebrow { text-transform: uppercase; font-size: 10.5px; letter-spacing: 0.13em; font-weight: 800; }
.idzl-h1 { font-size: 23px; font-weight: 800; letter-spacing: -0.02em; margin: 2px 0 0; color: #fff; }
.idzl-h2 { font-size: 25px; font-weight: 800; letter-spacing: -0.025em; margin: 4px 0 0; }
.idzl-fig { font-variant-numeric: tabular-nums; letter-spacing: -0.02em; font-weight: 700; }

.idzl-btn { border: 0; border-radius: 9px; padding: 10px 14px; font-size: 13px; font-weight: 700;
  cursor: pointer; display: inline-flex; align-items: center; gap: 7px; }
.idzl-quick { text-align: left; background: #fff; border: 1px solid var(--idzl-line); border-radius: 11px;
  padding: 13px 14px; cursor: pointer; display: flex; align-items: center; gap: 10px; width: 100%;
  transition: border-color 140ms ease, background 140ms ease; }
.idzl-quick:hover { border-color: #C6D6F5; background: #FBFCFF; }
.idzl-row { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-top: 1px solid var(--idzl-line-soft); }
.idzl-row:first-child { border-top: 0; }
.idzl-row-btn { margin-left: auto; background: transparent; border: 0; cursor: pointer; display: inline-flex;
  align-items: center; gap: 4px; font-size: 12px; font-weight: 700; }

.idzl-nav { display: flex; align-items: center; gap: 11px; padding: 10px 12px; border-radius: 10px;
  color: #DCE8FF; font-size: 13.5px; font-weight: 600; }
.idzl-nav-on { background: rgba(255,255,255,0.16); color: #fff; }

.idzl-root button:focus-visible, .idzl-root [tabindex]:focus-visible {
  outline: 2px solid #1D4ED8; outline-offset: 2px; }
@media (prefers-reduced-motion: reduce) {
  .idzl-root * { transition-duration: 0.01ms !important; }
  .idzl-card-open:hover { transform: none; }
}
`;

function ensureStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = css;
  document.head.appendChild(el);
}

/* ============================= demo data ============================== */
const DEMO_DATA = {
  realm: "Idenza",
  updatedAt: "11:06 AM",

  views: [
    {
      key: "acquiring",
      href: "/dashboard?realm=Idenza",
      icon: FiShoppingBag,
      title: "Merchant acquiring",
      subtitle: "Sponsor bank portfolio, merchant health command center",
      cta: "Open command center",
      blurb:
        "VAMP posture across the sponsored book, per merchant health scoring, and the chargeback and fraud ratios that move the acquirer level number.",
      status: { label: "At risk", tone: "purple" },
      accent: T.blue,
      accentSoft: T.blueSoft,
      metrics: [
        { label: "Portfolio VAMP", value: "55", unit: "bps", tone: T.red, note: "Standard 50" },
        { label: "Health score", value: "65", unit: "/100", tone: T.purple, note: "Volume weighted" },
        { label: "Merchants", value: "2", unit: "", tone: T.text, note: "1 above standard" },
        { label: "Open actions", value: "3", unit: "", tone: T.amber, note: "Reserve, review" },
      ],
      /* posture strip: value against the two VAMP thresholds */
      strip: { kind: "threshold", value: 55, standard: 50, excessive: 150, caption: "Portfolio VAMP against the 50 and 150 bps thresholds" },
    },
    {
      key: "approval",
      href: "/approval?realm=Idenza",
      icon: FiTrendingUp,
      title: "Approval & deflection",
      subtitle: "Merchant proof, before and after Idenza",
      cta: "Open the proof",
      blurb:
        "One merchant's month replayed with and without Idenza scoring pre-authorization: good customers recovered via 3DS, fraud denied before it settles, and the cost of both shown honestly.",
      status: { label: "Demo", tone: "blue" },
      accent: T.green,
      accentSoft: T.greenSoft,
      metrics: [
        { label: "Approval lift", value: "+14", unit: "pts", tone: T.green, note: "74.3 to 88.3%" },
        { label: "VAMP", value: "77", unit: "bps", tone: T.green, note: "from 251, under 150" },
        { label: "Net impact", value: "+$17.3", unit: "K", tone: T.green, note: "vs today, per month" },
        { label: "3DS abandoned", value: "60", unit: "", tone: T.amber, note: "lost sales, shown" },
      ],
      strip: {
        kind: "threshold",
        value: 77, standard: 50, excessive: 150,
        caption: "Merchant VAMP with Idenza, from 251 bps to under the 150 line",
      },
    },
    {
      key: "stablecoin",
      href: "/stablecoin/risk",
      icon: FiDollarSign,
      title: "Stablecoin programs",
      subtitle: "Wallet screening, KYT and exposure",
      cta: "Open screening",
      blurb:
        "Every transfer screened before settlement, exposure traced by hop distance from the source, and payouts held with the evidence for holding them.",
      status: { label: "Watch", tone: "amber" },
      accent: T.teal,
      accentSoft: T.tealSoft,
      metrics: [
        { label: "Settled, 30d", value: "$412.8", unit: "M", tone: T.text, note: "4 programs" },
        { label: "Direct exposure", value: "$1.24", unit: "M", tone: T.amber, note: "0.30% of value" },
        { label: "Sanctions hits", value: "7", unit: "", tone: T.red, note: "OFAC SDN" },
        { label: "Payouts held", value: "43", unit: "", tone: T.text, note: "$2.9M withheld" },
      ],
      /* posture strip: exposure by hop distance */
      strip: {
        kind: "hops",
        segments: [
          { label: "Direct", value: 2.22, color: T.red },
          { label: "1 hop", value: 5.93, color: T.amber },
          { label: "2 hops", value: 6.39, color: "#3F8E92" },
          { label: "3+ hops", value: 6.17, color: "#9BC4C6" },
        ],
        caption: "Tainted exposure by distance from the source, USD millions",
      },
    },
    {
      key: "sar",
      href: "/cases?realm=Idenza",
      icon: FiShield,
      title: "SAR automation",
      subtitle: "Agentic alert triage and filing",
      cta: "Open the queue",
      blurb:
        "Agents work each alert the way an analyst would, pull the corroborating activity across the account, and draft the SAR narrative with its evidence attached, ready for a human to review and file.",
      status: { label: "Demo", tone: "blue" },
      accent: T.purple,
      accentSoft: T.purpleSoft,
      metrics: [
        { label: "Alerts triaged", value: "1,284", unit: "", tone: T.text, note: "30d, auto-worked" },
        { label: "SARs drafted", value: "37", unit: "", tone: T.purple, note: "human-reviewed" },
        { label: "Hours saved", value: "210", unit: "hrs", tone: T.green, note: "at ~5.7 / case" },
        { label: "Median draft", value: "9", unit: "min", tone: T.text, note: "from ~4 hrs" },
      ],
      strip: {
        kind: "hops",
        segments: [
          { label: "Auto-cleared", value: 71, color: T.green },
          { label: "Agent-drafted", value: 22, color: T.purple },
          { label: "To analyst", value: 7, color: T.amber },
        ],
        caption: "Where alerts resolve, share of 30-day volume",
      },
    },
  ],

  attention: [
    { id: "a1", severity: "high", view: "acquiring", text: "Merchant B is at 78 bps, above the 50 bps standard for a third consecutive month.", action: "Review reserve" },
    { id: "a2", severity: "high", view: "stablecoin", text: "SDN match on a direct counterparty. $196,400 held on Global payouts.", action: "Open hold" },
    { id: "a6", severity: "high", view: "sar", text: "12 alerts aged past 24 hours are queued for agent triage; 3 are drafting as SAR-worthy.", action: "Open the queue" },
    { id: "a3", severity: "medium", view: "stablecoin", text: "Beneficiary data incomplete on 8.6% of outbound transfers, against a 95% target.", action: "See travel rule" },
    { id: "a5", severity: "medium", view: "approval", text: "Nutra cohort approval is at 74%, roughly 9 points of good volume recoverable via 3DS step-up.", action: "Open the proof" },
    { id: "a4", severity: "medium", view: "acquiring", text: "Chargeback rate on Merchant B reached 0.71%, crossing the internal threshold.", action: "Open merchant" },
  ],

  /* paths match the Navigator routes; the case management link is external and
   * is flagged so the handler can open it in a new tab instead of routing */
  quickLinks: [
    { label: "Rules", href: "/rules", icon: FaFileAlt },
    { label: "Sandbox", href: "/sandbox", icon: FaFlask },
    { label: "Execution results", href: "/execution-results", icon: FaList },
    { label: "Case management", href: "/cases", icon: FaBriefcase, addon: true, external: true },
  ],
};

/* ============================== primitives ============================= */
function Pill({ children, tone = "neutral" }) {
  const map = {
    neutral: [T.lineSoft, T.muted],
    blue: [T.blueSoft, T.blue],
    purple: [T.purpleSoft, T.purple],
    red: [T.redSoft, T.red],
    amber: [T.amberSoft, T.amber],
    green: [T.greenSoft, T.green],
    teal: [T.tealSoft, T.teal],
  };
  const [bg, fg] = map[tone] || map.neutral;
  return (
    <span className="idzl-eyebrow" style={{ background: bg, color: fg, padding: "4px 9px", borderRadius: 6, whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

/* threshold strip: where a value sits against standard and excessive */
function ThresholdStrip({ s }) {
  const pct = (v) => Math.min(100, (v / (s.excessive * 1.15)) * 100);
  const over = s.value > s.standard;
  return (
    <div>
      <div style={{ position: "relative", height: 10, borderRadius: 5, background: T.lineSoft, marginTop: 4 }}>
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pct(s.value)}%`,
          borderRadius: 5, background: over ? T.red : T.green }} />
        <div style={{ position: "absolute", left: `${pct(s.standard)}%`, top: -3, bottom: -3, width: 2, background: T.amber }} />
        <div style={{ position: "absolute", left: `${pct(s.excessive)}%`, top: -3, bottom: -3, width: 2, background: T.red }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontFamily: T.mono, fontSize: 10.5, color: T.muted }}>
        <span>0</span><span>50 standard</span><span>150 excessive</span>
      </div>
      <p style={{ fontSize: 11, color: T.muted, margin: "6px 0 0" }}>{s.caption}</p>
    </div>
  );
}

/* hop strip: proportional segments, closest to the source first */
function HopStrip({ s }) {
  const total = s.segments.reduce((a, x) => a + x.value, 0) || 1;
  return (
    <div>
      <div style={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", marginTop: 4 }}>
        {s.segments.map((seg) => (
          <div key={seg.label} title={`${seg.label} · $${seg.value.toFixed(2)}M`}
            style={{ width: `${(seg.value / total) * 100}%`, background: seg.color }} />
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontFamily: T.mono, fontSize: 10.5, color: T.muted }}>
        {s.segments.map((seg) => <span key={seg.label}>{seg.label}</span>)}
      </div>
      <p style={{ fontSize: 11, color: T.muted, margin: "6px 0 0" }}>{s.caption}</p>
    </div>
  );
}

function ViewCard({ view, onOpen }) {
  const Icon = view.icon;
  const open = () => onOpen && onOpen(view.key, view.href);
  return (
    <section
      className="idzl-card idzl-card-open"
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); } }}
      aria-label={`Open ${view.title}`}
    >
      <div style={{ padding: "20px 22px 0", flex: 1 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 13 }}>
          <div style={{ width: 42, height: 42, borderRadius: 11, background: view.accentSoft, color: view.accent,
            display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>
            <Icon size={21} aria-hidden="true" />
          </div>
          <div style={{ minWidth: 0 }}>
            <h3 style={{ fontSize: 19, fontWeight: 800, letterSpacing: "-0.02em", margin: 0, color: T.text }}>
              {view.title}
            </h3>
            <p style={{ fontSize: 12.5, color: T.muted, margin: "2px 0 0" }}>{view.subtitle}</p>
          </div>
          <span style={{ marginLeft: "auto" }}><Pill tone={view.status.tone}>{view.status.label}</Pill></span>
        </div>

        <p style={{ fontSize: 13, color: "#3E4C63", lineHeight: 1.55, margin: "14px 0 0" }}>{view.blurb}</p>

        <div className="idzl-metrics">
          {view.metrics.map((m) => (
            <div key={m.label} style={{ background: "#FAFCFF", border: `1px solid ${T.lineSoft}`, borderRadius: 10, padding: "10px 11px" }}>
              <div className="idzl-eyebrow" style={{ color: T.muted, fontSize: 9.5 }}>{m.label}</div>
              <div className="idzl-fig" style={{ fontFamily: T.mono, fontSize: 19, color: m.tone, marginTop: 4 }}>
                {m.value}<span style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>{m.unit}</span>
              </div>
              <div style={{ fontSize: 10.5, color: T.muted, marginTop: 2 }}>{m.note}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 16 }}>
          {view.strip.kind === "threshold" ? <ThresholdStrip s={view.strip} /> : <HopStrip s={view.strip} />}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 22px 20px" }}>
        <button className="idzl-btn" onClick={(e) => { e.stopPropagation(); open(); }}
          style={{ background: view.accent, color: "#fff", fontFamily: T.sans }}>
          {view.cta || "Open dashboard"} <FiArrowRight size={15} aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}

function MockSidebar() {
  const items = [
    { label: "Rules", icon: FaFileAlt },
    { label: "Rulesets", icon: FaList },
    { label: "Sandbox", icon: FaFlask },
    { label: "Execution Results", icon: FaList },
    { label: "Launch", icon: FiShoppingBag, on: true },
  ];
  return (
    <aside className="idzl-side">
      <div style={{ background: T.blueBright, borderRadius: 14, padding: "16px 12px", height: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 4px 14px" }}>
          <div className="idzl-logo" style={{ width: 34, height: 34, fontSize: 18 }}>I</div>
          <span style={{ color: "#fff", fontSize: 17, fontWeight: 800, letterSpacing: "-0.01em" }}>Navigator</span>
        </div>
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.25)", paddingTop: 12, display: "grid", gap: 4 }}>
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <div key={it.label} className={`idzl-nav${it.on ? " idzl-nav-on" : ""}`}>
                <Icon size={16} aria-hidden="true" />{it.label}
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
}

/* ================================ page ================================ */
export default function NavigatorLaunch({
  data = DEMO_DATA,
  routes,
  onOpen,
  onQuickLink,
  user,
  demoShell = false,
}) {
  useEffect(() => { ensureStyles(); }, []);

  const vars = { "--idzl-line": T.line, "--idzl-line-soft": T.lineSoft };
  /* routes override the default hrefs so the visible link and the navigation
   * target never drift apart */
  const views = routes
    ? data.views.map((v) => (routes[v.key] ? { ...v, href: routes[v.key] } : v))
    : data.views;
  const sevTone = { high: "red", medium: "amber", low: "neutral" };
  const sevIcon = { high: FiShield, medium: FiAlertTriangle, low: FiClock };
  const viewName = (key) => (views.find((v) => v.key === key) || {}).title || key;

  const page = (
    <div className="idzl-page">
      <div className="idzl-wrap">
        <header className="idzl-banner">
          <div className="idzl-logo">I</div>
          <div style={{ minWidth: 0, flex: 1, textAlign: "left" }}>
            <div className="idzl-eyebrow" style={{ color: "#AFC7F5", textAlign: "left" }}>IDENZA NAVIGATOR</div>
            <h1 className="idzl-h1" style={{ textAlign: "left" }}>
              {user ? `Good morning, ${user}` : "Risk command centers"}
            </h1>
            <p style={{ color: "#C9D9F7", fontSize: 13.5, margin: "4px 0 0", textAlign: "left" }}>
              One policy set across every program. Open the one you are working in.
            </p>
          </div>
          <div style={{ marginLeft: "auto", textAlign: "right" }}>
            <span style={{ background: "rgba(255,255,255,0.16)", color: "#fff", fontSize: 12, fontWeight: 700,
              padding: "6px 12px", borderRadius: 999, display: "inline-block" }}>
              Realm · {data.realm}
            </span>
            <div style={{ color: "#AFC7F5", fontSize: 11.5, marginTop: 8, fontFamily: T.mono }}>
              Last updated {data.updatedAt}
            </div>
          </div>
        </header>

        <div style={{ marginTop: 22 }}>
          <div className="idzl-eyebrow" style={{ color: T.blue }}>LAUNCH</div>
          <h2 className="idzl-h2" style={{ color: T.text }}>Choose a book</h2>
        </div>

        <div className="idzl-cards">
          {views.map((v) => <ViewCard key={v.key} view={v} onOpen={onOpen} />)}
        </div>

        <div style={{ marginTop: 26 }}>
          <div className="idzl-eyebrow" style={{ color: T.blue }}>NEEDS ATTENTION</div>
          <h2 className="idzl-h2" style={{ color: T.text, fontSize: 21 }}>Open across the platform</h2>
        </div>

        <div className="idzl-card" style={{ marginTop: 12 }}>
          {data.attention.length === 0 ? (
            <div style={{ padding: "34px 16px", textAlign: "center" }}>
              <p style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>Nothing open</p>
              <p style={{ fontSize: 12.5, color: T.muted, margin: "4px 0 0" }}>
                Every program is inside appetite. New items appear here as they are raised.
              </p>
            </div>
          ) : (
            data.attention.map((a) => {
              const Icon = sevIcon[a.severity] || FiClock;
              const view = views.find((v) => v.key === a.view);
              return (
                <div key={a.id} className="idzl-row">
                  <Icon size={16} color={a.severity === "high" ? T.red : T.amber} aria-hidden="true" />
                  <span style={{ fontSize: 13, color: T.text, minWidth: 0 }}>{a.text}</span>
                  <span style={{ marginLeft: 8 }}><Pill tone={a.severity === "high" ? "red" : "amber"}>{viewName(a.view)}</Pill></span>
                  <button className="idzl-row-btn" style={{ color: view ? view.accent : T.blue, fontFamily: T.sans }}
                    onClick={() => onOpen && onOpen(a.view, view && view.href)}>
                    {a.action} <FiChevronRight size={14} aria-hidden="true" />
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div style={{ marginTop: 26 }}>
          <div className="idzl-eyebrow" style={{ color: T.muted }}>ELSEWHERE IN NAVIGATOR</div>
          <div className="idzl-links">
            {data.quickLinks.map((q) => {
              const Icon = q.icon;
              return (
                <button key={q.label} className="idzl-quick" onClick={() => onQuickLink && onQuickLink(q.href, q)}
                  style={{ fontFamily: T.sans }}>
                  <span style={{ width: 30, height: 30, borderRadius: 8, background: T.blueSoft, color: T.blue,
                    display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>
                    <Icon size={15} aria-hidden="true" />
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{q.label}</span>
                  {q.addon && <span style={{ marginLeft: "auto" }}><Pill>Add on</Pill></span>}
                </button>
              );
            })}
          </div>
        </div>

        <p style={{ fontSize: 11.5, color: T.muted, marginTop: 22 }}>
          Scores run in one direction across every view: higher means more risk. Weights and
          thresholds are set by the bank and versioned with the policy.
        </p>
      </div>
    </div>
  );

  return (
    <div className="idzl-root" style={{ ...vars, background: T.page, color: T.text, fontFamily: T.sans, minHeight: "100%" }}>
      {demoShell ? <div className="idzl-shell"><MockSidebar />{page}</div> : page}
    </div>
  );
}
